import {
    FlexiCancelButton,
    FormPageLayout,
    PlatformLoader,
} from '@flexinet/ui-components';
import { PlatformServicePlan } from '@flexinet/pricing-components';
import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Card,
    Group,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import {
    NavLink,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useApplicationDetail } from '../hooks/useApplicationDetail';
import { PlanChangeModal } from '../components/PlanChangeModal';
import { Product, toServicePlan } from '../types';
import { SubscriptionStatusInfo } from '../hooks/useSubscriptionStatus';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { determineCustomerType } from '../../../../utils/customerTypeDetection';
import { subscribeToProduct } from '../../subscriptions/services/SubscriptionsService';
import {
    fetchRecords as fetchAccountApplications,
    add as addAccountApplication,
} from '../../../accounts-management/account-applications/services/AccountApplicationsService';
import { AiOutlineAppstore } from 'react-icons/ai';
import { stage } from '../../../../config/stage';
import { BillingSetupCard } from '../../customers/components/BillingSetupCard';

type PlanAction = 'subscribe' | 'current' | 'upgrade' | 'downgrade';

const getPlanAction = (
    product: Product,
    statusByProductId: Record<string, SubscriptionStatusInfo>,
    allProducts: Product[],
): PlanAction => {
    const info = statusByProductId[product.id];
    if (info?.isSubscribed) return 'current';

    const currentProduct = allProducts.find(p => statusByProductId[p.id]?.isSubscribed);
    if (!currentProduct) return 'subscribe';

    const currentPrice = statusByProductId[currentProduct.id]?.amount ?? 0;
    const targetPrice = product.prices?.items?.[0]?.amount ?? 0;
    return targetPrice > currentPrice ? 'upgrade' : 'downgrade';
};

export const ApplicationDetailPage = () => {
    const { applicationId } = useParams<{ applicationId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userAccount: account } = useUserAccount();

    const {
        application,
        products,
        statusByProductId,
        isLoading,
    } = useApplicationDetail(applicationId as string);

    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
    const [targetProduct, setTargetProduct] = useState<Product | null>(null);
    const [planAction, setPlanAction] = useState<PlanAction>('subscribe');
    const [subscribingProductId, setSubscribingProductId] = useState<string | null>(null);

    const currentProduct = products.find(p => statusByProductId[p.id]?.isSubscribed) ?? null;

    const ensureAccountApplication = async (accountId: string, appId: string) => {
        try {
            const existing = await fetchAccountApplications({ accountId, applicationId: appId });
            if (existing?.items && existing.items.length > 0) return;
            await addAccountApplication({ accountId, applicationId: appId, accountApplicationStatus: 'ACTIVE' });
        } catch (e) {
            console.error('Failed to create account-application:', e);
        }
    };

    const handleDirectSubscribe = async (product: Product) => {
        const price = product.prices?.items?.[0];
        if (!price) return;

        const customerId = account?.customerId;
        if (!customerId) {
            notifications.show({
                title: 'Billing Setup Required',
                message: 'Please set up your billing profile using the banner above before subscribing.',
                color: 'orange',
            });
            return;
        }

        const customerType = determineCustomerType();

        // If payment is required, redirect to billing providers
        if (customerType === 'direct' && !product.freeTrial && Number(price.amount || 0) > 0) {
            navigate(`/billing/providers?productId=${product.id}&priceId=${price.id}&returnTo=${encodeURIComponent(`/products/app/${applicationId}`)}`);
            return;
        }

        setSubscribingProductId(product.id);
        try {
            const result = await subscribeToProduct(customerId, price.id, 1, queryClient);

            if (result) {
                // Auto-enable account-application
                if (account?.id && applicationId) {
                    await ensureAccountApplication(account.id, applicationId);
                }

                notifications.show({
                    title: 'Success',
                    message: `Successfully subscribed to ${product.name}`,
                    color: 'green',
                });

                navigate('/subscriptions');
            }
        } catch (error) {
            console.error('Subscription error:', error);
            notifications.show({
                title: 'Error',
                message: 'Failed to create subscription',
                color: 'red',
            });
        } finally {
            setSubscribingProductId(null);
        }
    };

    const handlePlanAction = (product: Product, action: PlanAction) => {
        if (action === 'current') return;

        if (action === 'subscribe' && !currentProduct) {
            handleDirectSubscribe(product);
            return;
        }

        // Upgrade or downgrade — show confirmation modal
        setTargetProduct(product);
        setPlanAction(action);
        openModal();
    };

    if (isLoading) {
        return <PlatformLoader />;
    }

    if (!application) {
        return <Text ta="center" c="dimmed" mt="xl">Application not found.</Text>;
    }

    const breadcrumbs = [
        { title: 'Home', href: '/dashboard' },
        { title: 'Products', href: '/products' },
        { title: application.name || 'Application' },
    ].map((item, index) => (
        <Box key={index}>
            {item.href ? <NavLink to={item.href}>{item.title}</NavLink> : item.title}
        </Box>
    ));

    const pageNavigation = (
        <Box p="xs">
            <Breadcrumbs separator="→" mt="xs">{breadcrumbs}</Breadcrumbs>
        </Box>
    );

    const tools = [
        <FlexiCancelButton key="close" onClick={() => navigate('/products')} />
    ];

    return (
        <FormPageLayout
            title={application.name || 'Application'}
            pageNavigation={pageNavigation}
            tools={tools}
        >
            <Stack gap="lg" p="md">
                {account && !account.customerId && (
                    <BillingSetupCard variant="banner" />
                )}
                <Card shadow="sm" padding="xl" radius="md" withBorder>
                    <Group gap="md">
                        {application.logoUrl ? (
                            <Avatar src={application.logoUrl} size="lg" radius="sm" />
                        ) : (
                            <Avatar size="lg" radius="sm" color="blue">
                                <AiOutlineAppstore size="1.5em" />
                            </Avatar>
                        )}
                        <Stack gap={4}>
                            <Title order={2}>{application.name}</Title>
                            {application.description && (
                                <Text size="md" c="dimmed">{application.description}</Text>
                            )}
                        </Stack>
                    </Group>
                </Card>

                <Title order={3}>Choose Your Plan</Title>

                {products.length > 0 ? (
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                        {products.map((product) => {
                            const action = getPlanAction(product, statusByProductId, products);
                            const prices = product.prices?.items?.map(p => ({ amount: p.amount, billingPeriod: p.billingPeriod }));
                            const plan = toServicePlan(product, prices);
                            const isSubscribing = subscribingProductId === product.id;

                            const actionButton = (() => {
                                switch (action) {
                                    case 'current':
                                        return <Button disabled variant="filled" color="green">Current Plan</Button>;
                                    case 'subscribe':
                                        return <Button variant="filled" loading={isSubscribing} onClick={() => handlePlanAction(product, action)}>Subscribe</Button>;
                                    case 'upgrade':
                                        return <Button variant="filled" color="blue" onClick={() => handlePlanAction(product, action)}>Upgrade</Button>;
                                    case 'downgrade':
                                        return <Button variant="light" onClick={() => handlePlanAction(product, action)}>Downgrade</Button>;
                                }
                            })();

                            return (
                                <PlatformServicePlan
                                    key={product.id}
                                    plan={plan}
                                    stage={stage}
                                    sections={{
                                        action: <Group justify="center">{actionButton}</Group>,
                                    }}
                                />
                            );
                        })}
                    </SimpleGrid>
                ) : (
                    <Text ta="center" c="dimmed">No plans available for this application.</Text>
                )}
            </Stack>

            <PlanChangeModal
                opened={modalOpened}
                onClose={closeModal}
                targetProduct={targetProduct}
                currentProduct={currentProduct}
                action={planAction}
                statusByProductId={statusByProductId}
            />
        </FormPageLayout>
    );
};
